class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this._hoverLeaveTimer = null;
    this._hoverNavMedia = window.matchMedia('(min-width: 990px) and (hover: hover)');
    this._onHeaderMouseEnter = this._onHeaderMouseEnter.bind(this);
    this._onHeaderMouseLeave = this._onHeaderMouseLeave.bind(this);
    this._onSummaryClick = this._onSummaryClick.bind(this);
    this._onHoverNavMediaChange = this._onHoverNavMediaChange.bind(this);
  }

  connectedCallback() {
    this._installDesktopHoverNav();
  }

  disconnectedCallback() {
    this._removeDesktopHoverNav();
  }

  _isDesktopHoverNav() {
    return this._hoverNavMedia.matches;
  }

  _installDesktopHoverNav() {
    if (this._hoverNavInstalled) return;
    this._hoverNavInstalled = true;
    this.mainDetailsToggle.addEventListener('mouseenter', this._onHeaderMouseEnter);
    this.mainDetailsToggle.addEventListener('mouseleave', this._onHeaderMouseLeave);
    const summary = this.mainDetailsToggle?.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', this._onSummaryClick, true);
    }
    this._onParentLinkClickBound = (e) => {
      if (!this._isDesktopHoverNav()) return;
      e.stopPropagation();
    };
    this.mainDetailsToggle?.querySelector('a.header__menu-item-parent-link')?.addEventListener('click', this._onParentLinkClickBound);
    this._hoverNavMedia.addEventListener('change', this._onHoverNavMediaChange);
  }

  _removeDesktopHoverNav() {
    if (!this._hoverNavInstalled) return;
    this._hoverNavInstalled = false;
    this.mainDetailsToggle.removeEventListener('mouseenter', this._onHeaderMouseEnter);
    this.mainDetailsToggle.removeEventListener('mouseleave', this._onHeaderMouseLeave);
    const summary = this.mainDetailsToggle?.querySelector('summary');
    summary?.removeEventListener('click', this._onSummaryClick, true);
    this.mainDetailsToggle?.querySelector('a.header__menu-item-parent-link')?.removeEventListener('click', this._onParentLinkClickBound);
    this._hoverNavMedia.removeEventListener('change', this._onHoverNavMediaChange);
    this._clearHoverLeaveTimer();
  }

  _clearHoverLeaveTimer() {
    if (this._hoverLeaveTimer) {
      clearTimeout(this._hoverLeaveTimer);
      this._hoverLeaveTimer = null;
    }
  }

  _onHeaderMouseEnter() {
    if (!this._isDesktopHoverNav()) return;
    this._clearHoverLeaveTimer();
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
      const summary = this.mainDetailsToggle.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'true');
    }
    this.classList.add('header-menu--hover');
  }

  _onHeaderMouseLeave() {
    if (!this._isDesktopHoverNav()) return;
    this._clearHoverLeaveTimer();
    this._hoverLeaveTimer = setTimeout(() => {
      this.mainDetailsToggle.querySelectorAll('details[open]').forEach((d) => {
        d.removeAttribute('open');
        const s = d.querySelector('summary');
        if (s) s.setAttribute('aria-expanded', 'false');
      });
      this.mainDetailsToggle.removeAttribute('open');
      const summary = this.mainDetailsToggle.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      this.classList.remove('header-menu--hover');
    }, 200);
  }

  /**
   * Desktop + fine pointer: avoid <summary> toggling on mouse click so the parent
   * collection link can navigate; keyboard / touch keep native toggle.
   */
  _onSummaryClick(event) {
    if (!this._isDesktopHoverNav()) return;
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    if (event.target.closest('a.header__menu-item-parent-link')) return;
    event.preventDefault();
  }

  _onHoverNavMediaChange() {
    if (!this._isDesktopHoverNav()) {
      this._clearHoverLeaveTimer();
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
